/**
 * План v2 — об'єднаний редактор плану + аналіз оптимізатора в одному рядку.
 *
 * Колонки (на годину):
 *   1. Год
 *   2. Ціна РДН (з бейджем «прогноз»)
 *   3. P (slider) — потужність КГУ
 *   4. V_eko (input) — обсяг для ЕкоСфери
 *   5. Прогноз ЕкоСфера (input, з client_forecasts)
 *   6. V_rdn (readonly) = free − V_eko
 *   7. Ваш profit (live, з /optimizer/quick-calc)
 *   8. Best (P / profit) — з /optimizer/{date}
 *   9. Δ profit — your − best
 *   10. Save кнопка / expand chevron
 *
 * Логіка:
 *   - power_min/max/step з kgu_params
 *   - self_needs з kgu_params.self_needs_estimate_kw
 *   - Toggle «Власні потреби» (header, default ON, localStorage)
 *   - free = P - (toggle ? self : 0)
 *   - V_eko capped to free (deficit warning)
 *   - V_rdn = free - V_eko (auto, readonly)
 */
import { type ClipboardEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Copy,
  HelpCircle,
  LockOpen,
  Megaphone,
  Save,
  Send,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { clientsApi } from "@/api/clients";
import { forecastsApi } from "@/api/forecasts";
import {
  type DayOptimum,
  optimizerApi,
  type QuickCalcOut,
} from "@/api/optimizer";
import { parametersApi } from "@/api/parameters";
import { plansApi } from "@/api/plans";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useCanEdit } from "@/hooks/useRole";
import { apiErrorOf } from "@/api/tgChannel";
import { fmt, hourLabelShifted, num, todayIso } from "@/lib/format";
import { cn } from "@/lib/utils";

interface RowDraft {
  power_kw: string;
  v_eko: string;
  forecast: string;
}

function shiftDate(iso: string, deltaDays: number): string {
  const d = new Date(iso);
  d.setDate(d.getDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}

const SELF_NEEDS_TOGGLE_KEY = "plan2-count-self-needs";

/** Парсинг числа з комірки Excel/Sheets: прибирає пробіли-роздільники тисяч
 * (\s покриває звичайний пробіл та нерозривні), кому як десятковий → крапка. */
function parsePastedNumber(raw: string): number | null {
  const cleaned = raw.replace(/\s/g, "").replace(/,/g, ".");
  if (cleaned === "") return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

export function PlanV2Page() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canEdit = useCanEdit();
  // Пост плану в Telegram-канал — і менеджер, і admin (FR-TGC-02).
  const canPost = useCanEdit(["manager", "admin", "super_admin"]);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [sendToChannel, setSendToChannel] = useState(true);
  const [channelMsg, setChannelMsg] = useState<string | null>(null);

  const [planDate, setPlanDate] = useState(todayIso());
  const [countSelfNeeds, setCountSelfNeeds] = useState<boolean>(
    () => localStorage.getItem(SELF_NEEDS_TOGGLE_KEY) !== "0",
  );
  useEffect(() => {
    localStorage.setItem(SELF_NEEDS_TOGGLE_KEY, countSelfNeeds ? "1" : "0");
  }, [countSelfNeeds]);

  // ─── Data loads ───
  const planQ = useQuery({
    queryKey: ["plans", planDate],
    queryFn: () => plansApi.get(planDate),
  });
  const optQ = useQuery({
    queryKey: ["optimizer", planDate],
    queryFn: () => optimizerApi.day(planDate),
    retry: 0,
  });
  const kguQ = useQuery({
    queryKey: ["kgu-params", planDate],
    queryFn: () => parametersApi.getKguParams(planDate),
    retry: 0,
  });
  const forecastQ = useQuery({
    queryKey: ["forecasts", planDate],
    queryFn: () => forecastsApi.list(planDate),
  });
  const baselineEkoQ = useQuery({
    queryKey: ["forecasts-baseline", planDate, "ekosfera"],
    queryFn: () => forecastsApi.baseline(planDate, "ekosfera"),
    retry: 0,
  });
  const baselineEkoByHour = useMemo(() => {
    const map = new Map<number, number>();
    const data = baselineEkoQ.data?.by_hour;
    if (!data) return map;
    for (const [k, v] of Object.entries(data)) {
      const n = typeof v === "string" ? Number(v) : v;
      if (Number.isFinite(n)) map.set(Number(k), n);
    }
    return map;
  }, [baselineEkoQ.data]);
  const clientsQ = useQuery({
    queryKey: ["clients", { includeInactive: false }],
    queryFn: () => clientsApi.list(false),
  });

  const powerMin = Number(kguQ.data?.power_min_kw ?? 600);
  const powerMax = Number(kguQ.data?.power_max_kw ?? 1500);
  const powerStep = Number(kguQ.data?.power_step_kw ?? 10);
  const selfNeedsKw = Number(kguQ.data?.self_needs_estimate_kw ?? 20);
  const selfNeedsApplied = countSelfNeeds ? selfNeedsKw : 0;

  const submitted = !!planQ.data?.submitted_at;
  const readonly = !canEdit || submitted;

  const ekoClient = useMemo(
    () => (clientsQ.data ?? []).find((c) => c.code === "ekosfera"),
    [clientsQ.data],
  );

  // ─── Local draft state ───
  const [rows, setRows] = useState<Record<number, RowDraft>>({});
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  useEffect(() => {
    if (planQ.data === undefined || forecastQ.data === undefined) return;
    if (loadedFor === planDate) return;

    const next: Record<number, RowDraft> = {};
    // Defaults: всі 24 години з power_min, V_eko=0, forecast=""
    for (let h = 0; h < 24; h++) {
      next[h] = {
        power_kw: String(Math.round(powerMin)),
        v_eko: "0",
        forecast: "",
      };
    }
    // Перезапис з plan_hours.
    for (const ph of planQ.data?.hours ?? []) {
      const eko = Number(ph.volumes.ekosfera ?? 0);
      next[ph.hour] = {
        power_kw: String(Math.round(Number(ph.power_kw))),
        v_eko: String(Math.round(eko)),
        forecast: next[ph.hour]?.forecast ?? "",
      };
    }
    // Forecast'и з окремої таблиці.
    for (const f of forecastQ.data ?? []) {
      if (f.client_code !== "ekosfera") continue;
      next[f.hour] = {
        ...(next[f.hour] ?? { power_kw: String(powerMin), v_eko: "0", forecast: "" }),
        forecast: String(Math.round(Number(f.forecast_kwh))),
      };
    }
    setRows(next);
    setLoadedFor(planDate);
  }, [planQ.data, forecastQ.data, planDate, loadedFor, powerMin]);

  const reloadFromBackend = () => setLoadedFor(null);

  const setHour = (hour: number, patch: Partial<RowDraft>) => {
    setRows((s) => ({
      ...s,
      [hour]: { ...(s[hour] ?? { power_kw: "", v_eko: "", forecast: "" }), ...patch },
    }));
  };

  /** Вставка колонки з Excel/Sheets (Ctrl+V): значення з буфера розливаються
   * по годинах вниз, починаючи з `startHour`, у одне поле (power_kw або v_eko).
   * Розбиття по рядках (\n) + перша колонка до табуляції. */
  const pasteColumn = (
    startHour: number,
    field: "power_kw" | "v_eko",
    text: string,
  ) => {
    const lines = text.replace(/\r/g, "").split("\n");
    while (lines.length > 0 && lines[lines.length - 1].trim() === "") lines.pop();
    setRows((s) => {
      const next = { ...s };
      lines.forEach((line, i) => {
        const hour = startHour + i;
        if (hour > 23) return;
        const val = parsePastedNumber(line.split("\t")[0]);
        if (val === null) return;
        const cur = next[hour] ?? {
          power_kw: String(Math.round(powerMin)),
          v_eko: "0",
          forecast: "",
        };
        next[hour] = { ...cur, [field]: String(val) };
      });
      return next;
    });
  };

  // ─── Derived values per row ───
  function calcFreeRdn(P: number, V_eko: number): { free: number; v_rdn: number; deficit: number } {
    const free = Math.max(0, P - selfNeedsApplied);
    const allocated_eko = Math.min(V_eko, free);
    const v_rdn = Math.max(0, free - allocated_eko);
    const deficit = Math.max(0, V_eko - free);
    return { free, v_rdn, deficit };
  }

  // ─── Save mutations ───
  const saveHour = useMutation({
    mutationFn: async ({ hour, draft }: { hour: number; draft: RowDraft }) => {
      const P = Number(draft.power_kw || "0");
      const V_eko = Number(draft.v_eko || "0");
      const { v_rdn } = calcFreeRdn(P, V_eko);
      await plansApi.putHour(planDate, hour, {
        power_kw: P,
        volumes: {
          ekosfera: Math.min(V_eko, Math.max(0, P - selfNeedsApplied)),
          rdn: v_rdn,
        },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["plans", planDate] });
      void qc.invalidateQueries({ queryKey: ["metrics-hourly", planDate] });
      void qc.invalidateQueries({ queryKey: ["metrics-daily-h1"] });
    },
  });

  const saveAll = useMutation({
    mutationFn: async () => {
      // allSettled, а не all: якщо частина годин впаде (напр. помилка мережі),
      // не можна мовчки повертати "успіх" — інакше план зберігається неповним,
      // а користувач цього не бачить (саме так і втратилися 7 годин 04.06).
      const results = await Promise.allSettled(
        Array.from({ length: 24 }, (_, h) => {
          const draft = rows[h];
          if (!draft) return Promise.resolve();
          const P = Number(draft.power_kw || "0");
          const V_eko = Number(draft.v_eko || "0");
          const { v_rdn } = calcFreeRdn(P, V_eko);
          return plansApi.putHour(planDate, h, {
            power_kw: P,
            volumes: {
              ekosfera: Math.min(V_eko, Math.max(0, P - selfNeedsApplied)),
              rdn: v_rdn,
            },
          });
        }),
      );
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed > 0) throw new Error(String(failed));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["plans", planDate] });
      void qc.invalidateQueries({ queryKey: ["metrics-hourly", planDate] });
      void qc.invalidateQueries({ queryKey: ["metrics-daily-h1"] });
    },
    onError: (err) => {
      window.alert(t("plan2.save_all_error", { n: (err as Error).message }));
    },
  });

  const copyFromMutation = useMutation({
    mutationFn: (source: "yesterday" | "weekly_avg") =>
      plansApi.copyFrom(planDate, source),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["plans", planDate] });
      reloadFromBackend();
    },
  });

  const sentAt = (iso: string) => new Date(iso).toLocaleTimeString("uk-UA", { hour: "2-digit", minute: "2-digit" });

  const submitMutation = useMutation({
    mutationFn: () => plansApi.submit(planDate, sendToChannel),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ["plans", planDate] });
      setSubmitOpen(false);
      // План уже подано; помилка Telegram його не відкочує — лише попереджаємо.
      if (res.channel_post?.ok) setChannelMsg(t("plan.channel_sent", { time: sentAt(new Date().toISOString()) }));
      else if (res.channel_post) window.alert(t("plan.channel_failed", { error: res.channel_post.error ?? "" }));
    },
  });

  const channelPostMutation = useMutation({
    mutationFn: (force: boolean) => plansApi.channelPost(planDate, force),
    onSuccess: (res) =>
      setChannelMsg(t(res.is_edit ? "plan.channel_sent_edit" : "plan.channel_sent", { time: sentAt(res.sent_at) })),
    onError: (e) => {
      const { code, details } = apiErrorOf(e);
      if (code === "plan_post_unchanged") {
        const last = typeof details.last_sent_at === "string" ? sentAt(details.last_sent_at) : "";
        if (window.confirm(t("plan.channel_unchanged", { time: last }))) channelPostMutation.mutate(true);
      } else if (code === "channel_not_configured") {
        window.alert(t("plan.channel_not_configured"));
      } else {
        window.alert(t("plan.channel_failed", { error: String(details.detail ?? code ?? (e as Error).message) }));
      }
    },
  });

  // Зняти блокування «Подано в РДН» → план знову редагується (override A5).
  const unsubmitMutation = useMutation({
    mutationFn: () => plansApi.unsubmit(planDate),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["plans", planDate] }),
  });

  const applyBestAll = () => {
    if (!optQ.data) return;
    setRows((s) => {
      const next = { ...s };
      for (const h of optQ.data!.hours) {
        const cur = next[h.hour] ?? { power_kw: "", v_eko: "0", forecast: "" };
        next[h.hour] = { ...cur, power_kw: String(Math.round(h.best.power_kw)) };
      }
      return next;
    });
  };

  /** Застосувати auto-baseline (~N кВт·год same-DoW) як V_eko для всіх 24 годин. */
  const applyForecastAll = () => {
    if (baselineEkoByHour.size === 0) return;
    setRows((s) => {
      const next = { ...s };
      for (let h = 0; h < 24; h++) {
        const bl = baselineEkoByHour.get(h);
        if (bl === undefined) continue;
        const cur = next[h] ?? { power_kw: String(powerMin), v_eko: "0", forecast: "" };
        next[h] = { ...cur, v_eko: String(Math.round(bl)) };
      }
      return next;
    });
  };

  // ─── Totals ───
  const totals = useMemo(() => {
    let totalP = 0, totalEko = 0, totalRdn = 0, totalDeficit = 0;
    for (let h = 0; h < 24; h++) {
      const r = rows[h];
      if (!r) continue;
      const P = Number(r.power_kw || "0");
      const V_eko = Number(r.v_eko || "0");
      const { v_rdn, deficit } = calcFreeRdn(P, V_eko);
      totalP += P;
      totalEko += Math.min(V_eko, Math.max(0, P - selfNeedsApplied));
      totalRdn += v_rdn;
      totalDeficit += deficit;
    }
    return { totalP, totalEko, totalRdn, totalDeficit };
  }, [rows, selfNeedsApplied]);

  // ─── Render ───
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="min-w-0">{t("plan2.title")}</CardTitle>
            {/* Праворуч: спершу перемикач «Власні потреби», потім (найправіше) «?». */}
            <div className="ml-auto flex items-center gap-3">
              <button
                type="button"
                onClick={() => setCountSelfNeeds((v) => !v)}
                title={t("plan2.self_needs_hint", { kwh: selfNeedsKw })}
                className={cn(
                  "rounded-md border px-3 py-1.5 text-xs font-medium transition-colors",
                  countSelfNeeds
                    ? "border-brand-mint-300 bg-brand-mint-50 text-brand-mint-700"
                    : "border-slate-200 bg-white text-slate-500",
                )}
              >
                {countSelfNeeds ? "✓ " : ""}
                {t("plan2.self_needs_label")} {t("plan2.self_needs_toggle", { kwh: selfNeedsKw })}
              </button>
              <HelpHint />
            </div>
            <CardDescription className="basis-full">{t("plan2.subtitle")}</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-2">
          {/* Date з ‹ › */}
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPlanDate((d) => shiftDate(d, -1))}
              aria-label={t("home1.prev_day")}
              title={t("home1.prev_day")}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Input
              type="date"
              value={planDate}
              onChange={(e) => setPlanDate(e.target.value)}
              className="w-[150px]"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPlanDate((d) => shiftDate(d, 1))}
              aria-label={t("home1.next_day")}
              title={t("home1.next_day")}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Action buttons — компактні icon-only */}
          {!readonly && (
            <div className="flex flex-row gap-1 items-center">
              <Button
                variant="outline"
                size="icon"
                disabled={copyFromMutation.isPending}
                onClick={() => copyFromMutation.mutate("yesterday")}
                title={t("plan.copy_yesterday")}
                aria-label={t("plan.copy_yesterday")}
              >
                <Copy className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                disabled={copyFromMutation.isPending}
                onClick={() => copyFromMutation.mutate("weekly_avg")}
                title={t("plan.copy_weekly_avg")}
                aria-label={t("plan.copy_weekly_avg")}
              >
                <Calendar className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                disabled={!optQ.data}
                onClick={applyBestAll}
                title={t("plan2.apply_best_all")}
                aria-label={t("plan2.apply_best_all")}
              >
                <Sparkles className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                disabled={baselineEkoByHour.size === 0}
                onClick={applyForecastAll}
                title={t("plan2.apply_forecast_all")}
                aria-label={t("plan2.apply_forecast_all")}
              >
                <TrendingUp className="h-4 w-4" />
              </Button>
              <Button
                variant="default"
                size="sm"
                disabled={saveAll.isPending}
                onClick={() => saveAll.mutate()}
                title={t("plan2.save_all")}
              >
                <Save className="h-4 w-4" />
                {t("plan2.save_all")}
              </Button>
            </div>
          )}

          {/* Submit */}
          {canPost && (
            <div className="ml-auto flex items-center gap-2">
              {submitted && (
                <Button
                  variant="outline"
                  disabled={channelPostMutation.isPending}
                  onClick={() => channelPostMutation.mutate(false)}
                  title={t("plan.channel_post_hint")}
                >
                  <Megaphone className="h-4 w-4" />
                  {channelPostMutation.isPending ? t("plan.channel_sending") : t("plan.channel_post")}
                </Button>
              )}
              {canEdit && submitted && (
                <Button
                  variant="outline"
                  size="icon"
                  title={t("plan.unsubmit_hint")}
                  aria-label={t("plan.unsubmit_hint")}
                  disabled={unsubmitMutation.isPending}
                  onClick={() => {
                    if (window.confirm(t("plan.unsubmit_confirm"))) unsubmitMutation.mutate();
                  }}
                  className="text-amber-600 hover:text-amber-700 border-amber-300"
                >
                  <LockOpen className="h-4 w-4" />
                </Button>
              )}
              {canEdit && (
                <Button
                  variant={submitted ? "outline" : "default"}
                  disabled={submitted || submitMutation.isPending || saveAll.isPending}
                  onClick={() => {
                    setSendToChannel(true);
                    setSubmitOpen(true);
                  }}
                >
                  <Send className="h-4 w-4" />
                  {submitted
                    ? t("plan.submitted_banner", { time: new Date(planQ.data?.submitted_at ?? "").toLocaleTimeString("uk-UA") })
                    : t("plan.submit")}
                </Button>
              )}
            </div>
          )}
          {channelMsg && <div className="w-full text-right text-sm text-emerald-700">{channelMsg}</div>}
        </CardContent>
      </Card>

      {/* Діалог подачі з галочкою публікації в канал (FR-TGC-02). */}
      {submitOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" role="dialog" aria-modal="true">
          <Card className="w-full max-w-md">
            <CardContent className="space-y-4 p-6">
              <p className="text-sm text-slate-800">{t("plan.submit_confirm")}</p>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={sendToChannel}
                  onChange={(e) => setSendToChannel(e.target.checked)}
                />
                {t("plan.send_to_channel")}
              </label>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setSubmitOpen(false)} disabled={submitMutation.isPending}>
                  {t("common.cancel")}
                </Button>
                <Button onClick={() => submitMutation.mutate()} disabled={submitMutation.isPending}>
                  <Send className="h-4 w-4" />
                  {t("plan.submit")}
                </Button>
              </div>
              {submitMutation.isError && <p className="text-sm text-red-600">{t("common.error")}</p>}
            </CardContent>
          </Card>
        </div>
      )}

      {submitted && (
        <div className="rounded-md bg-emerald-50 border border-emerald-300 p-3 text-sm text-emerald-900">
          {t("plan.submitted_banner", {
            time: new Date(planQ.data!.submitted_at!).toLocaleTimeString("uk-UA"),
          })}
        </div>
      )}

      {/* Totals strip */}
      <Card>
        <CardContent className="flex flex-wrap gap-6 py-3 text-sm">
          <div>
            <span className="text-slate-500">Σ P · 1год: </span>
            <span className="font-mono font-medium">{fmt(totals.totalP)} кВт·год</span>
          </div>
          <div>
            <span className="text-slate-500">Σ ЕкоСфера: </span>
            <span className="font-mono font-medium">{fmt(totals.totalEko)} кВт·год</span>
          </div>
          <div>
            <span className="text-slate-500">Σ РДН: </span>
            <span className="font-mono font-medium">{fmt(totals.totalRdn)} кВт·год</span>
          </div>
          {totals.totalDeficit > 0 && (
            <div className="text-red-700">
              ⚠ Дефіцит ЕкоСфера: <span className="font-mono">{fmt(totals.totalDeficit)}</span> кВт·год
            </div>
          )}
        </CardContent>
      </Card>

      {/* Hours table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-slate-500">
                <th className="p-2 w-12">{t("common.hour")}</th>
                <th className="p-2 w-28">{t("optimizer.rdn_price")}</th>
                <th className="p-2 w-40">{t("plan.power_kw")}</th>
                <th className="p-2 w-24 text-right">{t("home1.volume_ekosfera")}</th>
                <th className="p-2 w-24 text-right">{t("home1.volume_rdn")}</th>
                <th className="p-2 w-32 text-right">{t("plan2.your_profit")}</th>
                <th className="p-2 w-32 text-right">{t("plan2.best")}</th>
                <th className="p-2 w-24 text-right">{t("plan2.delta")}</th>
                <th className="p-2 w-20" />
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 24 }, (_, h) => h).map((h) => (
                <HourRow
                  key={h}
                  hour={h}
                  draft={rows[h]}
                  onChange={(patch) => setHour(h, patch)}
                  onPasteColumn={pasteColumn}
                  onSave={() => rows[h] && saveHour.mutate({ hour: h, draft: rows[h] })}
                  saving={saveHour.isPending}
                  readonly={readonly}
                  optimizer={optQ.data}
                  planDate={planDate}
                  powerMin={powerMin}
                  powerMax={powerMax}
                  powerStep={powerStep}
                  selfNeedsApplied={selfNeedsApplied}
                  ekoPrice={Number(ekoClient?.tariff_type === "fixed" ? 9.5 : 0)} // fallback; точне з backend через quick-calc
                  baselineEko={baselineEkoByHour.get(h) ?? null}
                />
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Help hint (hover popover з описом кнопок/колонок) ───

function HelpHint() {
  const { t } = useTranslation();
  const lines = t("plan2.help.lines", { returnObjects: true }) as string[];
  return (
    <div className="relative group">
      <HelpCircle className="h-5 w-5 text-slate-400 group-hover:text-slate-600 cursor-help" />
      <div
        className="invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity
                   absolute right-0 top-7 z-50 w-96 max-w-[90vw] rounded-md border border-slate-200
                   bg-white p-3 text-xs leading-relaxed text-slate-600 shadow-xl space-y-1"
      >
        <div className="font-semibold text-slate-800">{t("plan2.help.title")}</div>
        {Array.isArray(lines) &&
          lines.map((l, i) => (
            <div key={i} className={l === "" ? "h-1" : ""}>
              {l}
            </div>
          ))}
      </div>
    </div>
  );
}

// ───────────────────────────── Row ────────────────────────────

interface HourRowProps {
  hour: number;
  draft: RowDraft | undefined;
  onChange: (patch: Partial<RowDraft>) => void;
  onPasteColumn: (startHour: number, field: "power_kw" | "v_eko", text: string) => void;
  onSave: () => void;
  saving: boolean;
  readonly: boolean;
  optimizer: DayOptimum | undefined;
  planDate: string;
  powerMin: number;
  powerMax: number;
  powerStep: number;
  selfNeedsApplied: number;
  ekoPrice: number;
  baselineEko: number | null;
}

function HourRow({
  hour, draft, onChange, onPasteColumn, onSave, saving, readonly,
  optimizer, planDate, powerMin, powerMax, powerStep, selfNeedsApplied,
  baselineEko,
}: HourRowProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  /** Снеп потужності на сітку кроку, ЗАКРІПЛЕНУ за powerMax (а не powerMin),
   * щоб максимум (power_max) завжди був досяжний навіть коли powerMin не кратний
   * кроку (напр. 601 + крок 10 інакше не дотягувався б до 1500). */
  const snapPower = (raw: number): number => {
    const v = Math.max(powerMin, Math.min(powerMax, raw));
    const k = Math.round((powerMax - v) / powerStep);
    return Math.max(powerMin, Math.min(powerMax, powerMax - k * powerStep));
  };

  /** Перехоплюємо вставку лише коли в буфері кілька рядків (колонка з Excel) —
   * тоді розливаємо вниз по годинах. Одиночне значення — звичайна вставка. */
  const handlePaste = (field: "power_kw" | "v_eko") =>
    (e: ClipboardEvent<HTMLInputElement>) => {
      const text = e.clipboardData.getData("text");
      if (/[\r\n]/.test(text.trim())) {
        e.preventDefault();
        onPasteColumn(hour, field, text);
      }
    };

  const opt = optimizer?.hours.find((h) => h.hour === hour);
  const P = Number(draft?.power_kw || "0");
  const V_eko_input = Number(draft?.v_eko || "0");
  const free = Math.max(0, P - selfNeedsApplied);
  const V_eko = Math.min(V_eko_input, free);
  const V_rdn = Math.max(0, free - V_eko);
  const deficit = Math.max(0, V_eko_input - free);
  // «Off» — КГУ вимкнено на цю годину: power=0 (явний 0 з поля або тоггл).
  // Об'єми тоді 0, поле/слайдер гасимо. Затрати/прибуток off-години = 0/0/0.
  const isOff = (draft?.power_kw ?? "") !== "" && P === 0;
  const toggleOff = () =>
    onChange(isOff ? { power_kw: String(powerMin) } : { power_kw: "0", v_eko: "0" });

  // ─── Live quick-calc (debounced) ───
  const [calc, setCalc] = useState<QuickCalcOut | null>(null);
  const [calcLoading, setCalcLoading] = useState(false);

  const runQuickCalc = useCallback(async () => {
    if (P <= 0) {
      setCalc(null);
      return;
    }
    setCalcLoading(true);
    try {
      const result = await optimizerApi.quickCalc({
        date: planDate,
        hour,
        power_kw: P,
        volumes: { ekosfera: V_eko, rdn: V_rdn },
        count_self_needs: selfNeedsApplied > 0,
      });
      setCalc(result);
    } catch {
      setCalc(null);
    } finally {
      setCalcLoading(false);
    }
  }, [P, V_eko, V_rdn, planDate, hour, selfNeedsApplied]);

  useEffect(() => {
    const handle = setTimeout(runQuickCalc, 300);
    return () => clearTimeout(handle);
  }, [runQuickCalc]);

  const yourProfit = calc?.profit_uah ?? null;
  const bestProfit = opt?.best.profit_uah ?? null;
  const delta = yourProfit !== null && bestProfit !== null ? yourProfit - bestProfit : null;

  return (
    <>
      <tr className={cn("border-t border-slate-100", deficit > 0 && "bg-red-50/40", isOff && "bg-slate-100/70")}>
        <td className="p-2 font-mono text-xs">{hourLabelShifted(hour)}</td>
        <td className="p-2 text-xs">
          {opt?.rdn_price_uah_per_mwh !== null && opt?.rdn_price_uah_per_mwh !== undefined ? (
            <span className="inline-flex items-center gap-1">
              {Math.round(opt.rdn_price_uah_per_mwh).toLocaleString("uk-UA")}
              {opt.rdn_price_is_forecast && (
                <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-medium text-amber-700">
                  {t("optimizer.rdn_price_forecast_badge")}
                </span>
              )}
            </span>
          ) : (
            <span className="text-slate-400">—</span>
          )}
        </td>
        <td className="p-2">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <Input
                type="number"
                step={powerStep}
                min={powerMin}
                max={powerMax}
                value={draft?.power_kw ?? ""}
                onChange={(e) => onChange({ power_kw: e.target.value })}
                onPaste={handlePaste("power_kw")}
                disabled={readonly || isOff}
                className={cn("h-8 text-xs", isOff && "text-slate-400")}
              />
              <Button
                type="button"
                size="sm"
                variant={isOff ? "default" : "outline"}
                onClick={toggleOff}
                disabled={readonly}
                title={t("plan2.off_toggle_hint")}
                className="h-8 px-2 text-[10px] shrink-0"
              >
                {isOff ? "On" : "Off"}
              </Button>
            </div>
            <input
              type="range"
              min={powerMin}
              max={powerMax}
              step={1}
              value={!isOff && draft?.power_kw ? draft.power_kw : powerMin}
              onChange={(e) => onChange({ power_kw: String(snapPower(Number(e.target.value))) })}
              disabled={readonly || isOff}
              className="w-full accent-brand-mint-500"
            />
            {isOff && (
              <span className="text-[10px] font-medium text-slate-500">{t("plan2.off_note")}</span>
            )}
          </div>
        </td>
        <td className="p-2 text-right">
          <Input
            type="number"
            step="10"
            min={0}
            value={draft?.v_eko ?? ""}
            onChange={(e) => onChange({ v_eko: e.target.value })}
            onPaste={handlePaste("v_eko")}
            disabled={readonly || isOff}
            className="h-8 text-xs w-full text-right"
            placeholder={baselineEko !== null ? `~ ${Math.round(baselineEko)}` : "—"}
          />
          {baselineEko !== null && (
            <div
              className="text-[10px] text-slate-400 mt-0.5 text-right"
              title={t("plan2.baseline_hint")}
            >
              ~ {Math.round(baselineEko)}
            </div>
          )}
          {deficit > 0 && !isOff && (
            <div className="text-[10px] text-red-600">−{Math.round(deficit)}</div>
          )}
        </td>
        <td className="p-2 text-right font-mono text-xs text-slate-600">
          {Math.round(V_rdn).toLocaleString("uk-UA")}
        </td>
        <td className="p-2 text-right font-mono text-xs">
          {calcLoading && yourProfit === null ? (
            <span className="text-slate-400">…</span>
          ) : yourProfit !== null ? (
            <span className={yourProfit >= 0 ? "text-emerald-700" : "text-red-700"}>
              {yourProfit >= 0 ? "+" : ""}
              {Math.round(yourProfit).toLocaleString("uk-UA")}
            </span>
          ) : (
            <span className="text-slate-400">—</span>
          )}
        </td>
        <td className="p-2 text-right font-mono text-xs">
          {opt ? (
            <span className="text-slate-600">
              {Math.round(opt.best.power_kw)} /{" "}
              <span className={opt.best.profit_uah >= 0 ? "text-emerald-700" : "text-red-700"}>
                {opt.best.profit_uah >= 0 ? "+" : ""}
                {Math.round(opt.best.profit_uah).toLocaleString("uk-UA")}
              </span>
            </span>
          ) : (
            <span className="text-slate-400">—</span>
          )}
        </td>
        <td className="p-2 text-right font-mono text-xs">
          {delta !== null ? (
            <span className={delta >= 0 ? "text-emerald-700" : "text-red-700"}>
              {delta >= 0 ? "+" : ""}
              {Math.round(delta).toLocaleString("uk-UA")}
            </span>
          ) : (
            <span className="text-slate-400">—</span>
          )}
        </td>
        <td className="p-2">
          <div className="flex items-center gap-1">
            {!readonly && (
              <Button size="sm" variant="outline" onClick={onSave} disabled={saving} className="h-7 px-2">
                <Save className="h-3 w-3" />
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setExpanded((v) => !v)}
              className="h-7 px-1"
            >
              {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </Button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-slate-50/40 border-b border-slate-200">
          <td colSpan={9} className="p-4">
            <ExpandedDetails
              hour={hour}
              date={planDate}
              calc={calc}
              opt={opt}
              powerMin={powerMin}
              powerMax={powerMax}
              currentP={P}
              onApplyBest={() => {
                if (opt && !readonly) {
                  onChange({ power_kw: String(Math.round(opt.best.power_kw)) });
                }
              }}
            />
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Expanded row: charts + breakdown ───

interface ExpandedProps {
  hour: number;
  date: string;
  calc: QuickCalcOut | null;
  opt: DayOptimum["hours"][number] | undefined;
  powerMin: number;
  powerMax: number;
  currentP: number;
  onApplyBest: () => void;
}

function ExpandedDetails({
  hour, date, calc, opt, powerMin, powerMax, currentP, onApplyBest,
}: ExpandedProps) {
  const { t } = useTranslation();
  const detailQ = useQuery({
    queryKey: ["optimizer", "hour", date, hour],
    queryFn: () => optimizerApi.hour(date, hour),
    retry: 0,
  });
  const detail = detailQ.data;

  const chartData = useMemo(() => {
    if (!detail) return [];
    return detail.scenarios.map((s) => ({
      power_kw: s.power_kw,
      profit_uah: s.profit_uah,
      cost_per_mwh: s.cost_uah_per_mwh,
    }));
  }, [detail]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Profit vs Power chart */}
        <div className="h-56 bg-white rounded border border-slate-200 p-2">
          <div className="text-xs text-slate-500 mb-1">{t("optimizer.chart_title_profit")}</div>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="90%">
              <LineChart data={chartData} margin={{ top: 5, right: 10, bottom: 15, left: 35 }}>
                <CartesianGrid stroke="#e2e8f0" />
                <XAxis dataKey="power_kw" type="number" domain={[powerMin, powerMax]} />
                <YAxis
                  width={65}
                  domain={[
                    (m: number) => Math.floor(m - Math.abs(m) * 0.1),
                    (m: number) => Math.ceil(m + Math.abs(m) * 0.1),
                  ]}
                />
                <Tooltip />
                <Line dataKey="profit_uah" stroke="#3ec79a" strokeWidth={2} dot={false} />
                {opt && (
                  <ReferenceDot
                    x={opt.best.power_kw}
                    y={opt.best.profit_uah}
                    r={5}
                    fill="#002060"
                    stroke="#fff"
                    strokeWidth={2}
                  />
                )}
                <ReferenceLine x={currentP} stroke="#94a3b8" strokeDasharray="3 3" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-xs text-slate-400">{t("common.loading")}</div>
          )}
        </div>
        {/* Cost vs Power chart */}
        <div className="h-56 bg-white rounded border border-slate-200 p-2">
          <div className="text-xs text-slate-500 mb-1">{t("optimizer.chart_title_cost")}</div>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="90%">
              <LineChart data={chartData} margin={{ top: 5, right: 10, bottom: 15, left: 35 }}>
                <CartesianGrid stroke="#e2e8f0" />
                <XAxis dataKey="power_kw" type="number" domain={[powerMin, powerMax]} />
                <YAxis
                  width={65}
                  domain={[
                    (m: number) => Math.floor(m * 0.95),
                    (m: number) => Math.ceil(m * 1.05),
                  ]}
                />
                <Tooltip />
                <Line dataKey="cost_per_mwh" stroke="#002060" strokeWidth={2} dot={false} />
                <ReferenceLine x={currentP} stroke="#94a3b8" strokeDasharray="3 3" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-xs text-slate-400">{t("common.loading")}</div>
          )}
        </div>
      </div>

      {/* Breakdown */}
      {calc && (
        <div className="text-xs text-slate-600 grid grid-cols-2 gap-x-8 gap-y-1 font-mono">
          <div>self_needs = {fmt(calc.energy.self_needs_kwh)} кВт·год</div>
          <div>free = {fmt(calc.energy.free_kwh)} кВт·год</div>
          <div>used = {fmt(calc.energy.used_kwh)} кВт·год</div>
          <div>leftover = {fmt(calc.energy.leftover_kwh)} кВт·год</div>
          <div>rdn_price = {calc.rdn_price_uah_per_mwh !== null ? fmt(calc.rdn_price_uah_per_mwh) : "—"} грн/МВт·год</div>
          <div>eko_tariff = {calc.eko_tariff_uah_per_kwh ? fmt(calc.eko_tariff_uah_per_kwh, 4) : "—"} грн/кВт·год</div>
          <div>revenue = {fmt(calc.revenue.total_uah)} грн</div>
          <div>cost/MWh = {fmt(calc.cost.cost_uah_per_mwh)} грн</div>
          <div className="col-span-2 border-t border-slate-200 pt-1 mt-1">
            profit = {fmt(num(calc.profit_uah))} грн
          </div>
          {calc.warnings.length > 0 && (
            <div className="col-span-2 text-amber-700">⚠ {calc.warnings.join(", ")}</div>
          )}
        </div>
      )}

      {/* Apply best button */}
      {opt && (
        <Button size="sm" variant="outline" onClick={onApplyBest}>
          <Sparkles className="h-3 w-3" /> {t("plan2.apply_best_hour", { p: Math.round(opt.best.power_kw) })}
        </Button>
      )}
    </div>
  );
}
